<template>
    <div class="blog-edit-container">
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="10" animated />
      </div>
  
      <div v-else class="blog-edit-content">
        <el-form :model="blogForm" label-width="80px" class="blog-form">
          <el-form-item label="标题">
            <el-input v-model="blogForm.title" placeholder="请输入文章标题" />
          </el-form-item>
  
          <el-form-item label="作者">
            <el-input 
              v-model="blogForm.authors" 
              placeholder="请输入作者名称，多个作者用逗号分隔"
            />
          </el-form-item>
  
          <el-form-item label="标签">
            <ContentTagSelect v-model="blogForm.tagIds" scope="blog" />
          </el-form-item>
  
          <el-form-item label="分类">
            <el-select
              v-model="blogForm.categories"
              multiple
              filterable
              allow-create
              default-first-option
              placeholder="请选择或输入分类"
            >
              <el-option
                v-for="category in availableCategories"
                :key="category"
                :label="category"
                :value="category"
              />
            </el-select>
          </el-form-item>
  
          <el-form-item label="封面图">
            <el-upload
              class="banner-uploader"
              action="/api/v1/upload"
              :headers="uploadHeaders"
              :data="uploadData"
              :show-file-list="false"
              :on-success="handleBannerSuccess"
              :before-upload="beforeBannerUpload"
            >
              <img v-if="blogForm.banner" :src="blogForm.banner" class="banner" />
              <el-icon v-else class="banner-uploader-icon"><Plus /></el-icon>
            </el-upload>
          </el-form-item>
  
          <el-form-item label="摘要">
            <el-input
              v-model="blogForm.summary"
              type="textarea"
              :rows="3"
              placeholder="请输入文章摘要"
            />
          </el-form-item>
  
          <el-form-item label="内容">
            <MonoEdit v-model="blogForm.content" height="min(75vh, 800px)" theme="vs-light" />
          </el-form-item>
  
          <el-form-item>
            <el-button type="primary" @click="handleSubmit">保存</el-button>
            <el-button @click="goBack">取消</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, computed } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { ElMessage } from 'element-plus'
  import { validatePhotoUpload, adminUploadFormData, uploadResponseToUrl } from '@/utils/photoUpload'
  import { Plus } from '@element-plus/icons-vue'
  import MonoEdit from '@/components/MonoEdit.vue'
  import ContentTagSelect from '@/components/ContentTagSelect.vue'
  import axios from 'axios'

  const route = useRoute()
  const router = useRouter()
  const loading = ref(true)
  const blogForm = ref({
    title: '',
    authors: [],
    tags: [],
    tagIds: [],
    categories: [],
    banner: '',
    summary: '',
    content: '',
    onlyAdmin: 0
  })
  const availableCategories = ref([])

  const uploadHeaders = computed(() => {
    const token = localStorage.getItem('jwt_token')
    return {
      'Authorization': `Bearer ${token}`
    }
  })

  const uploadData = computed(() => adminUploadFormData('blog'))

  const fetchBlog = async () => {
    try {
      loading.value = true
      const token = localStorage.getItem('jwt_token')
      const response = await axios.get(`/api/v1/blog/${route.params.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.data.code === 200) {
        const blog = response.data.data
        blogForm.value = {
          title: blog.title || '',
          authors: typeof blog.authors === 'string' ? blog.authors.split(',').map(author => author.trim()) : (blog.authors || []),
          tags: blog.tags || [],
          tagIds: (blog.tagIds || []).map((id) => Number(id)).filter((id) => id > 0),
          categories: blog.categories || [],
          banner: blog.banner || '',
          summary: blog.summary || '',
          content: blog.content || '',
          onlyAdmin: blog.onlyAdmin ?? 0
        }
      } else {
        ElMessage.error(response.data.message || '获取文章失败')
      }
    } catch (error) {
      console.error('Error fetching blog:', error)
      ElMessage.error('获取文章失败')
    } finally {
      loading.value = false
    }
  }

  const fetchTagsAndCategories = async () => {
    try {
      const token = localStorage.getItem('jwt_token')
      const [categoriesResponse] = await Promise.all([
        axios.post('/api/v1/blog/category/list', {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ])

      if (categoriesResponse.data.code === 200) {
        availableCategories.value = categoriesResponse.data.data.categories.map(cat => cat.category)
      }
    } catch (error) {
      console.error('Error fetching tags and categories:', error)
    }
  }

  const handleBannerSuccess = (response) => {
    if (response.code === 200) {
      blogForm.value.banner = uploadResponseToUrl(response)
      ElMessage.success('封面图上传成功')
    } else {
      ElMessage.error(response.message || '封面图上传失败')
    }
  }

  const beforeBannerUpload = (file) => validatePhotoUpload(file, ElMessage.error)

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('jwt_token')
      const submitData = {
        id: parseInt(route.params.id),
        ...blogForm.value,
        onlyAdmin: blogForm.value.onlyAdmin ?? 0,
        authors: Array.isArray(blogForm.value.authors) ? blogForm.value.authors : 
                (typeof blogForm.value.authors === 'string' ? blogForm.value.authors.split(',').map(author => author.trim()) : [])
      }
      const response = await axios.post('/api/v1/blog/update', submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.data.code === 200) {
        ElMessage.success('文章更新成功')
        router.push(`/blogs/${route.params.id}`)
      } else {
        ElMessage.error(response.data.message || '更新文章失败')
      }
    } catch (error) {
      console.error('Error updating blog:', error)
      ElMessage.error('更新文章失败')
    }
  }

  const goBack = () => {
    router.push(`/blogs/${route.params.id}`)
  }

  onMounted(() => {
    fetchBlog()
    fetchTagsAndCategories()
  })
  </script>
  
  <style scoped>
  .blog-edit-container {
    max-width: none;
    width: 100%;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
  }
  
  .loading-container {
    padding: 20px;
  }
  
  .blog-edit-content {
    background-color: #fff;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  }
  
  .blog-form {
    max-width: none;
    width: 100%;
    margin: 0 auto;
  }
  
  .banner-uploader {
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: var(--el-transition-duration-fast);
  }
  
  .banner-uploader:hover {
    border-color: var(--el-color-primary);
  }
  
  .banner-uploader-icon {
    font-size: 28px;
    color: #8c939d;
    width: 178px;
    height: 178px;
    text-align: center;
    line-height: 178px;
  }
  
  .banner {
    width: 178px;
    height: 178px;
    display: block;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    .blog-edit-container {
      padding: 10px;
    }
  
    .blog-edit-content {
      padding: 20px;
    }
  
  }
  </style>